import React from 'react';
import { Helmet } from 'react-helmet';
import { datadogRum } from '@datadog/browser-rum';

import { ENVIRONMENTS } from '../utils/constants/urls';

const config = require('../utils/constants/siteConfig');

/** Datadog initialization */
datadogRum.init({
  applicationId: process.env.DATADOG_APPLICATION_ID || '',
  clientToken: process.env.DATADOG_CLIENT_TOKEN || '',
  site: 'datadoghq.com',
  service: 'www',
  env: process.env.REACT_APP_ENV,
  //  version: '1.0.0',
  sampleRate: 100,
  trackInteractions: true,
  silentMultipleInit: true,
});

const schemaOrgJSONLD = {
  '@context': 'https://schema.org',
  '@type': 'Organization',
  name: 'Everlywell',
  legalName: 'Everly Well, Inc.',
  url: 'https://www.everlywell.com/',
  logo:
    'https://www.everlywell.com/images/logos/2019-rebrand/everlywell-logo-e3288d32.svg',
  foundingDate: '2015',
  founders: [
    {
      '@type': 'Person',
      name: 'Julia Cheek',
    },
  ],
  address: {
    '@type': 'PostalAddress',
    streetAddress: '823 Congress, Suite 1200',
    addressLocality: 'Austin',
    addressRegion: 'Texas',
    postalCode: '78701',
    addressCountry: 'United States',
  },
  contactPoint: {
    '@type': 'ContactPoint',
    contactType: 'customer support',
    telephone: '+1 (855) 422-3855',
    email: 'contact@everlywell.com',
  },
  sameAs: [
    'https://www.facebook.com/everlywell/',
    'https://twitter.com/everly_well',
    'https://www.pinterest.com/everlywell/',
    'https://www.linkedin.com/company/everlywell',
  ],
};

interface Props {
  title?: string;
  description?: string;
  keywords?: string;
  url?: string;
}

export const Head = ({ title, description, keywords, url }: Props) => (
  <Helmet htmlAttributes={{ lang: 'en' }}>
    {process.env.REACT_APP_ENV !== ENVIRONMENTS.PRODUCTION ? (
      <meta name="robots" content="noindex" />
    ) : null}
    {/* Start VWO Async SmartCode */}
    <script
      async
      data-cfasync="false"
      src="https://dev.visualwebsiteoptimizer.com/lib/533964.js"
    ></script>
    {/* This is the Mixpanel integration with VWO.
            It needs to be included on the page if the Experiment is running
            on a particular page */}
    <script type="text/javascript">
      {`
        var dataSendingTimer;
        var tryPushingDataTimer;
        var _vis_data = {};

        window._CURRENT_CAMPAIGNS_EW_VWO = [];
        window.VWO = window.VWO || [];

        window.VWO.push([
          "onVariationApplied",
          function (data) {
            if (!data) return;

            var expId = data[1];
            var variationId = data[2];

            var isExperimentDefined =
              typeof _vwo_exp[expId].comb_n[variationId] != "undefined";
            var isEventVisualExperiment =
              ["VISUAL_AB", "VISUAL", "SPLIT_URL", "SURVEY"].indexOf(
                _vwo_exp[expId].type
              ) > -1;

            if (isExperimentDefined && isEventVisualExperiment) {
              var test = "VWO-Test-ID-" + expId;
              var variant = _vwo_exp[expId].comb_n[variationId];

              window._CURRENT_CAMPAIGNS_EW_VWO.push({
                test: test,
                variant: variant,
              });
            }
          },
        ]);
      `}
    </script>
    {/* General tags */}
    <title>{title || config.title}</title>
    <meta name="description" content={description || config.siteDescription} />
    <meta name="keywords" content={keywords || config.keywords} />
    <link rel="canonical" href={url || config.siteUrl} />
    {/* Schema.org tags */}
    <script type="application/ld+json">
      {JSON.stringify(schemaOrgJSONLD)}
    </script>
    {/* OpenGraph tags */}
    <meta property="og:title" content={title || config.title} />
    <meta property="og:type" content="website" />
    <meta property="og:url" content={url || config.siteUrl} />
    <meta
      property="og:description"
      content={description || config.siteDescription}
    />
    {/* Twitter Card tags */}
    <meta name="twitter:card" content="summary_large_image" />
    <meta
      name="twitter:creator"
      content={config.userTwitter ? config.userTwitter : ''}
    />
    <meta name="twitter:title" content={title || config.siteTitle} />
    <meta
      name="twitter:description"
      content={description || config.siteDescription}
    />
  </Helmet>
);
